import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { Flex } from '@revenuemarket/cds';
import { colorTokens, theme } from '@revenuemarket/design';
import { Tooltip } from 'antd';

import InfoLine from '../Icon/v3/components/InfoLine';
import Typography from '../Typography';

export type Item = {
  /** item element */
  element: React.ReactNode;
  /** item action */
  action?: React.MouseEventHandler<HTMLElement>;
  class?: string;
};

interface HeaderProps {
  label: string;
  tooltip?: string;
}

interface Props {
  headers: HeaderProps[];
  /** table title */
  title: string;
  /** table item list */
  items: Item[][];
  /** 아이템들이 없을시에 보여주는 문구 */
  emptyDescription?: string;
  /** 테이블의 가로 크기 */
  gridTemplateColumns: string;
  /** 테이블의 행에 border를 넣을지 여부 */
  rowBorder?: boolean;
  /** 테이블의 위 끝에 border를 넣을지 여부 */
  bottomBorder?: boolean;
  /** 아이템들의 padding */
  itemPadding?: string;
  borderColor?: string;
  emptyDescriptionStyle?: React.CSSProperties;
}

const Wrapper = styled.table<{
  borderColor?: string;
}>`
  width: 100%;
  border-top: 1px solid ${({ borderColor }) => borderColor || theme.colors.grayscale650};
  border-bottom: 1px solid ${({ borderColor }) => borderColor || theme.colors.grayscale650};
  tbody > tr:last-child {
    border-bottom: none;
  }
`;

const Header = styled.th`
  display: flex;
  align-items: center;
  gap: 4px;
  text-align: left;
  padding: 12px 10px;
`;

const Row = styled.tr<{ gridTemplateColumns: string; rowBorder: boolean; borderColor?: string }>`
  display: grid;
  align-items: center;
  padding: 0px 0px;
  ${({ rowBorder, borderColor }) =>
    rowBorder && `border-bottom: 1px solid ${borderColor || theme.colors.grayscale650};`}

  grid-template-columns: ${({ gridTemplateColumns }) => gridTemplateColumns};
`;

const Item = styled.td<{ padding?: string }>`
  padding: ${({ padding }) => (padding ? padding : '14px 10px')};
`;

const EmptyDescription = styled.tr`
  display: flex;
  justify-content: center;
  padding: 40px 0px;
`;

const Table: React.FC<Props> = ({
  headers,
  title,
  items,
  emptyDescription = '데이터가 없습니다',
  emptyDescriptionStyle,
  gridTemplateColumns,
  rowBorder = true,
  bottomBorder = true,
  itemPadding,
  borderColor,
}) => {
  const [domLoaded, setDomLoaded] = useState(false);

  // hydration error 방지용
  useEffect(() => {
    setDomLoaded(true);
  }, []);

  return domLoaded ? (
    <>
      {title && (
        <Flex style={{ marginBottom: 16 }}>
          <Typography variant="body2" color={theme.colors.grayscale400}>
            {title}
          </Typography>
        </Flex>
      )}
      <Wrapper borderColor={borderColor}>
        <thead>
          <Row gridTemplateColumns={gridTemplateColumns} rowBorder={true} borderColor={borderColor}>
            {headers.map((header) => {
              return (
                <Header key={header.label}>
                  <Typography variant="caption1" color={theme.colors.grayscale400}>
                    {header.label}
                  </Typography>
                  {header.tooltip && (
                    <Tooltip title={header.tooltip}>
                      <InfoLine width={16} height={16} />
                    </Tooltip>
                  )}
                </Header>
              );
            })}
          </Row>
        </thead>
        <tbody>
          {items.length ? (
            items.map((item, index) => {
              return (
                <Row
                  gridTemplateColumns={gridTemplateColumns}
                  key={index}
                  rowBorder={rowBorder}
                  borderColor={borderColor}
                >
                  {item.map((item, index) => {
                    return (
                      <Item
                        className={item.class}
                        onClick={item.action}
                        key={index}
                        padding={itemPadding}
                      >
                        {item.element}
                      </Item>
                    );
                  })}
                </Row>
              );
            })
          ) : (
            <EmptyDescription>
              <td>
                <Typography
                  variant="h3"
                  color={colorTokens.textDescription}
                  style={emptyDescriptionStyle}
                >
                  {emptyDescription}
                </Typography>
              </td>
            </EmptyDescription>
          )}
        </tbody>
      </Wrapper>
    </>
  ) : (
    <div />
  );
};

export default Table;
